import Distance from 'components/Distance';
import { __ } from 'i18n/index';
import loadedCharacterOf, { loadedRolledCharacterOf } from 'react/match/rankedPanel/loadedCharacterOf';
import { opponentsOf, teammatesOf, teamNameOf, isDoublesMatch } from 'react/match/rankedPanel/matchTeams';

// A player's first main for a ladder, or null.
export function mainFor(player, ladderId) {
	const ladderGame = player?.ladder_information?.games?.get(ladderId);
	if(!ladderGame || !ladderGame.mains) {
		return null;
	}
	const first = ladderGame.mains.values().next();
	return first.done ? null : first.value;
}

// The character for a player's stock icon: the one picked for the current
// game once it is in the character cache, else their main for the match's game.
export function characterFor(match, player) {
	if(!player) {
		return null;
	}
	return loadedCharacterOf(match.game, player) || mainFor(player, match.ladder?.id);
}

// The distance to a player as the severity (1–5) and its label, or null.
export function distanceTo(player) {
	const distance = player?.location?.distanceFromUser;
	if(!distance) {
		return null;
	}
	const severity = distance.getDistanceSeverity();
	return { severity, label: Distance.getDistanceDescriptionById(severity) };
}

// Ranked or friendly and singles or doubles, as one phrase per combination so a
// translation can reorder the words and make them agree.
function teamModeNameOf(match) {
	const doubles = Number(match.team_size) > 1;
	if(match.is_ranked) {
		return doubles ? __('Ranked doubles') : __('Ranked singles');
	}
	return doubles ? __('Friendly doubles') : __('Friendly singles');
}

// "Ranked singles · Best of 3"
export function modeNameOf(match) {
	return `${teamModeNameOf(match)} · ${match.getTotalMatchesName()}`;
}

function codeOf(player, type) {
	return player?.connected_fields?.[type]?.primary_value || null;
}

// On a ladder where players connect by entering each other's code (Melee:
// Slippi IDs), the field's name and both players' codes, else null.
export function playerCodesFor(match, me, opponent) {
	const type = match.ladder?.player_code_type;
	if(!type) {
		return null;
	}
	const field = (match.ladder.connected_field_types || []).find((fieldType) => fieldType.type === type);
	return {
		type,
		name: field ? field.name : __('Code'),
		mine: codeOf(me, type),
		opponent: codeOf(opponent, type),
	};
}

// How the players connect: both players' codes on a ladder where they enter
// each other's (playerCodes), else who hosts and the code they posted
// (connectCode, null until posted). hasCodes is false on a ladder with
// neither.
export function connectInfoFor(match, me, opponent) {
	const playerCodes = playerCodesFor(match, me, opponent);
	const hostCode = !playerCodes && match.host_code && match.host_code.insert_player_id ? match.host_code : null;
	const host = playerCodes ? null : hostCode?.player || match.player1 || null;
	const usesArenaCodes = !!match.usesArenaCodes?.();
	return {
		hasCodes: !!match.ladder?.uses_named_rooms || !!playerCodes || !!match.ladder?.has_host_code || usesArenaCodes,
		playerCodes,
		connectCode: hostCode ? hostCode.code : null,
		host,
		hostName: host ? host.username : null,
		usesArenaCodes,
		opponent: opponent || null,
		opponentName: opponent ? opponent.username : __('your opponent'),
	};
}

function outcomeOf(isEnded, myScore, opponentScore) {
	if(!isEnded || myScore === null || myScore === opponentScore) {
		return null;
	}
	return myScore > opponentScore ? 'won' : 'lost';
}

// What the match room shows about the current match: both players with their
// stock icons, the set score, and the GAME / DISTANCE / CONNECT CODE / HOST
// fields, or both players' codes on a ladder where no one hosts.
// In doubles, opponent is the first of the other team and opponentName both
// of them ("Moonwalk & Moss"); teammates holds my partner.
export default function describeMatchRoom(match) {
	const me = match.players?.[myUser.id]?.player || myUser;
	const opponents = opponentsOf(match);
	const opponent = opponents[0] || null;
	const hasScores = !!match.set_score;
	const myScore = hasScores ? match.getMyTeamWinCount() : null;
	const opponentScore = hasScores ? match.getOtherTeamWinCount() : null;
	// Match.isEnded() is also true while a dispute is open, but that set isn't settled yet.
	const isDisputed = !!match.disputeIsActive?.();
	const isEnded = !!match.isEnded() && !isDisputed;
	const connect = connectInfoFor(match, me, opponent);

	return {
		me,
		opponent,
		opponents,
		teammates: teammatesOf(match),
		isDoubles: isDoublesMatch(match),
		opponentName: teamNameOf(opponents, __('your opponent')),
		myMain: characterFor(match, me),
		opponentMain: characterFor(match, opponent),
		// What a Random pick for the current game landed on, else null.
		myRolled: loadedRolledCharacterOf(match.game, me),
		opponentRolled: loadedRolledCharacterOf(match.game, opponent),
		myScore,
		opponentScore,
		isEnded,
		isDisputed,
		outcome: outcomeOf(isEnded, myScore, opponentScore),
		gameName: match.ladder?.name || '',
		modeName: modeNameOf(match),
		distance: distanceTo(opponent),
		connectCode: connect.connectCode,
		hostName: connect.hostName,
		playerCodes: connect.playerCodes,
		// The program the ladder is played on (Brawlback) and its download page.
		client: match.ladder?.client || null,
	};
}
